import { getVAProjects, getProjectReports } from '@/services/projectService';
import {
  getVATasks,
  getVATaskLists,
} from '@/services/virtualAssistantsService';
import { getSocket } from '@/services/clientSessionService';
import { formatShortDate } from '@/services/formattingService';
import { billSearch } from '@/services/vaBillsService';
import { timeSearch } from '@/services/timeService';
import { useUserStore } from './user';
import moment from 'moment';
import apm from 'vg-shared/apm';

import { defineStore } from 'pinia';

export const useVaDashboardStore = defineStore('vaDashboard', {
  state: () => ({
    projects: [],
    vaRelationships: [],
    vaInvoiceAdjustments: [],
    vaBills: {
      tableOptions: {
        itemsPerPage: 10,
        page: 1,
        sortBy: [{ key: 'bill_date', order: 'desc' }],
      },
      resetPagination: false,
      tableRows: [],
      rowCount: 0,
      filterForm: {},
    },
    vaTime: {
      tableOptions: {
        page: 1,
        itemsPerPage: 10,
        sortBy: [
          { key: 'task_date', order: 'desc' },
          { key: 'created_date', order: 'asc' },
        ],
        groupBy: [],
      },
      resetPagination: false,
      tableRows: [],
      rowCount: 0,
      totalTime: 0,
      totalBillableTime: 0,
      totalNonBillableTime: 0,
      filterForm: {},
    },
    vaTasks: {
      tableRows: [],
      rowCount: 0,
      filterForm: {},
    },
    vaTaskListSelections: { lists: [], rowCount: 0 },
    vaTasksToDo: {
      tableOptions: {
        page: 1,
        itemsPerPage: 10,
        sortBy: [
          { key: 'next_due_date', order: 'asc' },
          { key: 'priority', order: 'asc' },
        ],
        groupBy: [],
      },
      resetPagination: false,
      tableRows: [],
      rowCount: 0,
    },
    vaCompletedTasks: {
      tableOptions: {
        page: 1,
        itemsPerPage: 10,
        sortBy: [{ key: 'completed_date', order: 'desc' }],
        groupBy: [],
      },
      resetPagination: false,
      tableRows: [],
      rowCount: 0,
      filterForm: {},
    },
    vaReports: {
      tableOptions: {
        page: 1,
        itemsPerPage: 10,
        sortBy: [{ key: 'end_date', order: 'desc' }],
        groupBy: [],
      },
      resetPagination: false,
      tableRows: [],
      rowCount: 0,
      filterForm: {},
    },
  }),
  getters: {
    taskDateQuickPickOptions() {
      return [
        { title: this.$t('portal:time.overdue.heading'), value: 'overdue' },
        { title: this.$t('portal:time.today.label'), value: 'today' },
        { title: this.$t('portal:time.tomorrow.label'), value: 'tomorrow' },
        { title: this.$t('portal:time.endOfWeek.label'), value: 'end_week' },
      ];
    },
    completedDateQuickPickOptions() {
      return [
        { title: this.$t('portal:time.yesterday.label'), value: 'yesterday' },
        { title: this.$t('portal:time.today.label'), value: 'today' },
        { title: this.$t('portal:time.thisWeek.label'), value: 'this_week' },
      ];
    },
    vaOrganizedProjects() {
      const projectHeaders = [
        { title: this.$t('portal:projectName.heading'), value: 'project_name' },
        { title: this.$t('portal:client.heading'), value: 'client_uuid' },
        { title: this.$t('portal:roles.heading'), value: 'roles' },
        {
          title: this.$t('portal:project.va.committedHours.heading'),
          value: 'monthly_hours_committed',
          align: 'center',
        },
        {
          title: this.$t('portal:project.remainingHours.heading'),
          value: 'va_hours_remaining',
          align: 'center',
          vg_filter: 'Current',
        },
        {
          title: this.$t('portal:project.va.additionalHours.heading'),
          value: 'additional_hours',
          align: 'center',
          vg_filter: 'Current',
        },
        {
          title: this.$t('portal:project.invoiceDay.heading'),
          value: 'invoice_day',
          align: 'center',
        },
        {
          title: this.$t('portal:date.start.label'),
          value: 'rate_start_date',
        },
        { title: this.$t('portal:date.end.label'), value: 'end_date' },
      ];
      const now = formatShortDate(new Date());
      return this.projects.reduce(
        (accumulator, project) => {
          if (
            project.rate_start_date > now &&
            (!project.rp_end_date || project.rp_end_date > now)
          ) {
            accumulator.upcoming.projects.push(project);
          } else if (
            (project.end_date && //va rate plan end date
              project.end_date < now &&
              !accumulator.current.projects.find(
                (currentProject) => currentProject.uuid === project.uuid
              )) ||
            (project.rp_end_date && // project rate plan end date
              project.rp_end_date < now &&
              !accumulator.current.projects.find(
                (currentProject) => currentProject.uuid === project.uuid
              ))
          ) {
            accumulator.past.projects.push(project);
          } else if (
            project.rate_start_date &&
            project.rate_start_date <= now &&
            (!project.end_date || project.end_date >= now)
          ) {
            accumulator.current.projects.push(project);
            accumulator.current.projects.sort(
              (a, b) => a.days_remaining - b.days_remaining
            );
          }
          return accumulator;
        },
        {
          upcoming: {
            label: this.$t('portal:time.upcoming.label'),
            type: 'upcoming',
            projects: [],
            header: projectHeaders.filter(
              (header) => !header.vg_filter || header.vg_filter === 'Upcoming'
            ),
          },
          current: {
            label: this.$t('portal:time.current.label'),
            type: 'current',
            projects: [],
            header: projectHeaders.filter(
              (header) => !header.vg_filter || header.vg_filter === 'Current'
            ),
          },
          past: {
            label: this.$t('portal:time.past.label'),
            type: 'past',
            projects: [],
            header: projectHeaders.filter(
              (header) => !header.vg_filter || header.vg_filter === 'Past'
            ),
          },
        }
      );
    },
    projectRelationships() {
      return this.vaRelationships
        ?.map((r) => ({
          company: r.company,
          project_uuid: r.project_uuid,
          project_name: r.project_name,
          invoice_day: r.invoice_day,
          va_lead: r.va_lead,
          has_logged_time: r.has_logged_time,
        }))
        .sort((a, b) => a.company.localeCompare(b.company));
    },
    vaProjectSelections() {
      return this.vaRelationships?.map((r) => ({
        client_uuid: r.client_uuid,
        company: r.company,
        project_uuid: r.project_uuid,
        project_name: r.project_name,
        active: r.active,
      }));
    },
    vaTaskLists() {
      return Array.from(
        this.vaTasks.tableRows
          .reduce((lists, task) => {
            const listId =
              task.project_task_list_uuid || task.project_uuid + '_default';
            const list = lists.get(listId) || {
              uuid: listId,
              name: task.project_task_list_name,
              created_by: task.project_task_list_created_by,
              created_by_user_name: task.project_task_list_created_by_user_name,
              created_date: task.project_task_list_created_date,
              client_uuid: task.client_uuid,
              company: task.company,
              project_uuid: task.project_uuid,
              project_name: task.project_name,
              tasks: [],
            };
            list.tasks.push(task);
            lists.set(listId, list);
            return lists;
          }, new Map())
          .values()
      )
        .sort((a, b) => a.company.localeCompare(b.company))
        .concat(
          this.vaTaskListSelections.lists
            .filter(
              (list) =>
                list.created_by === useUserStore().user.uuid &&
                !this.vaTasks.tableRows.some(
                  (task) => task.project_task_list_uuid === list.uuid
                )
            )
            .map((list) => {
              return { ...list, tasks: [] };
            })
        );
    },
  },
  actions: {
    async initVADashboard(payload) {
      const userStore = useUserStore();
      const vaDashboardProjectUpdateListener = async () => {
        try {
          const result = await getVAProjects(userStore.user.uuid, true);
          this.projects = result.projects;
          this.vaInvoiceAdjustments = result.invoiceAdjustments;
          this.vaRelationships = result.vaRelationships;
        } catch (error) {
          apm(error);
        }
      };
      if (
        !getSocket()
          .listeners('talentplace.projects_disabled_time_entry_updated')
          ?.some((f) => f.name === 'vaDashboardProjectUpdateListener')
      ) {
        getSocket().on(
          'talentplace.projects_disabled_time_entry_updated',
          vaDashboardProjectUpdateListener
        );
        getSocket().on(
          'talentplace.reload_va_projects',
          vaDashboardProjectUpdateListener
        );
      }

      if (userStore.hasSession) {
        if (this.projects.length && !payload?.forceReload) {
          return;
        }
        const result = await getVAProjects(
          userStore.user.uuid,
          payload?.dashboard
        ).catch((error) => {
          apm(error);
          return {
            data: { projects: [] },
          };
        });
        this.projects = result.projects;
        this.vaInvoiceAdjustments = result.invoiceAdjustments;
        this.vaRelationships = result.vaRelationships;
      } else {
        location.href = '/';
      }
    },
    async billSearch(payload) {
      let caughtError;
      const searchResult = await billSearch(
        [
          `pagination.limit=${this.vaBills.tableOptions.itemsPerPage}`,
          `pagination.offset=${
            (this.vaBills.tableOptions.page - 1) *
            this.vaBills.tableOptions.itemsPerPage
          }`,
          this.vaBills.tableOptions.sortBy
            .map((sortBy) => `pagination.sortBy[]=${sortBy.key}`)
            .join('&'),
          this.vaBills.tableOptions.sortBy
            .map((sortBy) => `pagination.sortDesc[]=${sortBy.order === 'desc'}`)
            .join('&'),
          ...payload.filters,
        ].filter((x) => x)
      ).catch((error) => {
        apm(error);
        caughtError = error;
        return { bills: [], rowCount: 0 };
      });
      this.updateVaBills({
        tableRows: searchResult.bills,
      });
      if (searchResult.rowCount !== undefined) {
        this.updateVaBills({
          rowCount: searchResult.rowCount,
        });
      }
      if (caughtError) {
        throw caughtError;
      }
    },
    async timeSearch(payload) {
      let caughtError;
      const searchResult = await timeSearch(
        [
          `pagination.limit=${this.vaTime.tableOptions.itemsPerPage}`,
          `pagination.offset=${
            (this.vaTime.tableOptions.page - 1) *
            this.vaTime.tableOptions.itemsPerPage
          }`,
          this.vaTime.tableOptions.sortBy
            .map((sortBy) => `pagination.sortBy[]=${sortBy.key}`)
            .join('&'),
          this.vaTime.tableOptions.sortBy
            .map((sortBy) => `pagination.sortDesc[]=${sortBy.order === 'desc'}`)
            .join('&'),
          ...payload.filters,
        ].filter((x) => x)
      ).catch((error) => {
        apm(error);
        caughtError = error;
        return { bills: [], rowCount: 0 };
      });

      this.updateVaTime({
        tableRows: searchResult.time,
      });

      if (searchResult.rowCount !== undefined) {
        this.updateVaTime({
          rowCount: searchResult.rowCount,
        });
      }

      if (caughtError) {
        throw caughtError;
      }
    },
    async taskSearch(payload) {
      let caughtError;
      const taskSearchResult = await getVATasks(
        [...payload.filters].filter((x) => x)
      ).catch((error) => {
        apm(error);
        caughtError = error;
        return { tasks: [], rowCount: 0 };
      });

      // Determine a default timer id for each task. This is used on the task pages
      // to start & track timers related to tasks. Project timers still use just the
      // project UUID.
      taskSearchResult.tasks.forEach(
        (task) => (task.timer_id = `${task.project_uuid}${task.uuid || ''}`)
      );

      // Update the main tasks collection
      this.updateVaTasks({
        tableRows: taskSearchResult.tasks,
        rowCount: taskSearchResult.rowCount,
      });

      // Update the todo collection
      this.updateVaTasksToDo({
        tableRows: taskSearchResult.tasks,
      });

      // Get all the task lists (even if there are no tasks on them)
      const listSearchResult = await getVATaskLists().catch((error) => {
        apm(error);
        caughtError = error;
        return { lists: [], rowCount: 0 };
      });

      this.vaTaskListSelections = listSearchResult;

      if (caughtError) {
        throw caughtError;
      }
    },
    async completedTaskSearch(payload) {
      let caughtError;
      const searchResult = await getVATasks(
        [...payload.filters].filter((x) => x)
      ).catch((error) => {
        apm(error);
        caughtError = error;
        return { tasks: [], rowCount: 0 };
      });

      // Update the main tasks collection
      this.updateVaCompletedTasks({
        tableRows: searchResult.tasks,
        rowCount: searchResult.rowCount,
      });

      if (caughtError) {
        throw caughtError;
      }
    },
    setTaskDateQuickPick(selected) {
      switch (selected) {
        case 'overdue':
          this.updateVaTasks({
            filterForm: {
              ...this.vaTasks.filterForm,
              due_from_date: undefined,
              due_to_date: moment().subtract(1, 'day').format('YYYY-MM-DD'),
            },
          });
          break;
        case 'today':
          this.updateVaTasks({
            filterForm: {
              ...this.vaTasks.filterForm,
              due_from_date: moment().format('YYYY-MM-DD'),
              due_to_date: moment().format('YYYY-MM-DD'),
            },
          });
          break;
        case 'tomorrow':
          this.updateVaTasks({
            filterForm: {
              ...this.vaTasks.filterForm,
              due_from_date: moment().add(1, 'day').format('YYYY-MM-DD'),
              due_to_date: moment().add(1, 'day').format('YYYY-MM-DD'),
            },
          });
          break;
        case 'end_week':
          this.updateVaTasks({
            filterForm: {
              ...this.vaTasks.filterForm,
              due_from_date: undefined,
              due_to_date: moment().isoWeekday(5).format('YYYY-MM-DD'),
            },
          });
          break;
        default:
          this.updateVaTasks({
            filterForm: {
              ...this.vaTasks.filterForm,
              due_from_date: undefined,
              due_to_date: undefined,
            },
          });
          break;
      }
    },
    setCompletedDateQuickPick(selected) {
      switch (selected) {
        case 'yesterday':
          this.updateVaCompletedTasks({
            filterForm: {
              ...this.vaCompletedTasks.filterForm,
              completed_from_date: moment()
                .subtract(1, 'day')
                .format('YYYY-MM-DD'),
              completed_to_date: moment()
                .subtract(1, 'day')
                .format('YYYY-MM-DD'),
            },
          });
          break;
        case 'today':
          this.updateVaCompletedTasks({
            filterForm: {
              ...this.vaCompletedTasks.filterForm,
              completed_from_date: moment().format('YYYY-MM-DD'),
              completed_to_date: moment().format('YYYY-MM-DD'),
            },
          });
          break;
        case 'this_week':
          this.updateVaCompletedTasks({
            filterForm: {
              ...this.vaCompletedTasks.filterForm,
              completed_from_date: moment().isoWeekday(1).format('YYYY-MM-DD'),
              completed_to_date: moment().isoWeekday(5).format('YYYY-MM-DD'),
            },
          });
          break;
        default:
          this.updateVaCompletedTasks({
            filterForm: {
              completed_from_date: undefined,
              completed_to_date: undefined,
            },
          });
          break;
      }
    },
    async eowReportSearch(payload) {
      let caughtError;
      const searchResult = await getProjectReports([...payload.filters]).catch(
        (error) => {
          apm(error);
          caughtError = error;
          return { reports: [], rowCount: 0 };
        }
      );
      this.updateVaReports({
        tableRows: searchResult.reports,
      });

      if (searchResult.rowCount) {
        this.updateVaReports({
          rowCount: searchResult.rowCount,
        });
      }

      if (caughtError) {
        throw caughtError;
      }
    },
    updateVaBills(payload) {
      if (
        payload.filterForm !== undefined &&
        this.vaBills.tableOptions.page !== 1
      ) {
        payload.resetPagination = true;
      }
      this.vaBills = Object.assign(this.vaBills, payload);
    },
    updateVaTime(payload) {
      if (
        payload.filterForm !== undefined &&
        this.vaTime.tableOptions.page !== 1
      ) {
        payload.resetPagination = true;
      }
      this.vaTime = Object.assign(this.vaTime, payload);
    },
    updateVaTasks(payload) {
      this.vaTasks = Object.assign(this.vaTasks, payload);
    },
    updateVaTasksToDo(payload) {
      if (
        payload.filterForm !== undefined &&
        this.vaTasksToDo.tableOptions.page !== 1
      ) {
        payload.resetPagination = true;
      }
      this.vaTasksToDo = Object.assign(this.vaTasksToDo, payload);

      // Initial filtering to user assigned tasks or anyone
      let tableRows = [...this.vaTasks.tableRows].filter((task) => {
        return task.assignment.some(
          (a) => a.user_uuid === useUserStore().user?.uuid
        );
      });

      // Apply client-side sorting
      tableRows.sort((a, b) => {
        let result = 0,
          i = 0;
        while (result === 0 && this.vaTasksToDo.tableOptions.sortBy[i]) {
          const field = this.vaTasksToDo.tableOptions.sortBy[i].key;
          const desc =
            this.vaTasksToDo.tableOptions.sortBy[i].order === 'desc' || false;
          result = desc
            ? (b[field] === null) - (a[field] === null) ||
              +(b[field] > a[field]) ||
              -(b[field] < a[field])
            : (a[field] === null) - (b[field] === null) ||
              +(a[field] > b[field]) ||
              -(a[field] < b[field]);
          i++;
        }
        return result;
      });

      // Update table with paginated result
      this.vaTasksToDo = Object.assign(this.vaTasksToDo, {
        tableRows: tableRows.slice(
          (this.vaTasksToDo.tableOptions.page - 1) *
            this.vaTasksToDo.tableOptions.itemsPerPage,
          this.vaTasksToDo.tableOptions.page *
            this.vaTasksToDo.tableOptions.itemsPerPage
        ),
        rowCount: tableRows.length,
      });
    },
    updateVaCompletedTasks(payload) {
      this.vaCompletedTasks = Object.assign(this.vaCompletedTasks, payload);
    },
    updateVaReports(payload) {
      this.vaReports = Object.assign(this.vaReports, payload);
    },
  },
});
